/**
 * SearchEngine — filters table rows by matching a query string against
 * the visible text content of each cell.
 *
 * For cells containing a <select> element, the search reads the currently
 * selected option's text rather than the concatenated text of all options.
 * This resolves the bootstrap-table defect where searching matched against
 * every option in a dropdown.
 */

import { resolveField } from './utils'

export class SearchEngine {
  /**
   * Filter rows against a query string.
   *
   * For each row the method checks whether any visible column's cell text
   * contains the query (case-insensitive). If a rendered <tbody> is
   * available the actual DOM cell is inspected (so <select> selected values
   * are read correctly). When the tbody is unavailable or a corresponding
   * DOM row cannot be found, the method falls back to resolving the field
   * value directly from the row data object.
   *
   * @param {Object[]} rows    - All row data objects
   * @param {string}   query   - Search text entered by the user
   * @param {Object[]} columns - Column definitions (must include `field` and `visible`)
   * @param {HTMLElement|null} tbody - The rendered <tbody> element (may be null)
   * @returns {Object[]} Rows that match the query
   */
  static filter(rows, query, columns, tbody) {
    if (!query || query.trim() === '') {
      return rows
    }

    const visibleColumns = columns.filter((col) => {
      return col.visible !== false
    })

    // Split query into individual words for multi-word search.
    // A row matches if EVERY word appears in at least one cell across the row.
    const words = query.toLowerCase().split(/\s+/).filter((w) => w.length > 0)

    if (words.length === 0) {
      return rows
    }

    return rows.filter((row) => {
      // Build searchable text from visible column values AND common display fields
      // that formatters may render (e.g., bundle.name for Device Type column)
      const cellTexts = visibleColumns.map((col) => {
        const value = resolveField(row, col.field)
        return value != null ? String(value).toLowerCase() : ''
      })

      // Also include key fields that formatters display but aren't column fields
      const extraFields = ['bundle.name', 'name', 'deviceName', 'locationPath']
      extraFields.forEach((field) => {
        const value = resolveField(row, field)
        if (value != null) {
          cellTexts.push(String(value).toLowerCase())
        }
      })

      const rowText = cellTexts.join(' ')

      // Every search word must appear somewhere in the row's combined text
      return words.every((word) => rowText.includes(word))
    })
  }

  /**
   * Extract searchable text from a table cell DOM element.
   *
   * If the cell contains a <select> element the text of the first
   * selected option is returned. Otherwise the cell's full textContent
   * is returned.
   *
   * @param {HTMLElement} cell - A <td> element
   * @returns {string} The searchable text content
   */
  static getCellText(cell) {
    if (!cell) {
      return ''
    }

    const select = cell.querySelector('select')

    if (select && select.selectedOptions && select.selectedOptions.length > 0) {
      return select.selectedOptions[0].textContent || ''
    }

    return cell.textContent || ''
  }

  // ── Private helpers ──────────────────────────────────────────────

  /**
   * Resolve the searchable text for a single column in a row.
   *
   * Prefers reading from the DOM (via getCellText) when a rendered row is
   * available. Falls back to resolveField on the raw row data otherwise.
   *
   * @param {Object}   row            - The row data object
   * @param {Object}   col            - The column definition
   * @param {number}   colIndex       - Index of the column among visible columns
   * @param {HTMLElement|null} domRow - The corresponding <tr> in the tbody
   * @param {Object[]} visibleColumns - All currently visible column definitions
   * @returns {string}
   * @private
   */
  static _getCellTextForColumn(row, col, colIndex, domRow, visibleColumns) {
    if (domRow) {
      const cells = domRow.querySelectorAll('td')
      const cell = cells[colIndex]

      if (cell) {
        return SearchEngine.getCellText(cell)
      }
    }

    // Fallback: resolve the field value from the row data directly
    const value = resolveField(row, col.field)

    if (value == null) {
      return ''
    }

    return String(value)
  }
}
